import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Chip, Stack, Card, CardContent, Switch, FormLabel } from '@mui/joy';
import { Triangle, Info } from '@phosphor-icons/react';

interface FacetVisualizationProps {
  googleMapInstance?: google.maps.Map;
  facets: Array<{
    facet_id: string;
    area_sqft: number;
    pitch: string;
    pitch_degrees: number;
    azimuth: number;
    orientation_cardinal: string;
    facet_type: string;
    polygon_coordinates?: any;
  }>;
  center: { lat: number; lng: number };
  visible?: boolean;
}

interface FacetPolygon {
  polygon: google.maps.Polygon;
  facet: any;
  label?: google.maps.Marker;
}

const PITCH_COLORS = {
  low: '#4CAF50',      // Green for low pitch (< 4:12)
  medium: '#FFC107',   // Yellow for medium pitch (4:12 - 8:12)
  steep: '#F44336'     // Red for steep pitch (> 8:12)
};

export const FacetVisualization: React.FC<FacetVisualizationProps> = ({
  googleMapInstance,
  facets,
  center,
  visible = true
}) => {
  const [facetPolygons, setFacetPolygons] = useState<FacetPolygon[]>([]);
  const [showLabels, setShowLabels] = useState(true);
  const [colorByPitch, setColorByPitch] = useState(true);
  const [selectedFacet, setSelectedFacet] = useState<string | null>(null);

  // Get color based on pitch
  const getPitchColor = (pitchDegrees: number) => {
    if (pitchDegrees < 18.4) return PITCH_COLORS.low;    // < 4:12
    if (pitchDegrees < 33.7) return PITCH_COLORS.medium; // 4:12 - 8:12
    return PITCH_COLORS.steep;                           // > 8:12
  };

  // Get color based on orientation
  const getOrientationColor = (azimuth: number) => {
    // Map azimuth to hue (0-360 degrees to 0-360 hue)
    // North = blue, East = green, South = yellow/orange, West = red
    const hue = (azimuth + 180) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Create or update polygons
  useEffect(() => {
    if (!googleMapInstance || !visible) return;

    // Clear existing polygons
    facetPolygons.forEach(({ polygon, label }) => {
      polygon.setMap(null);
      label?.setMap(null);
    });

    const newPolygons: FacetPolygon[] = [];

    facets.forEach((facet) => {
      if (!facet.polygon_coordinates) return;

      // Convert coordinates to Google Maps LatLng
      let paths: google.maps.LatLng[] = [];
      
      if (Array.isArray(facet.polygon_coordinates)) {
        paths = facet.polygon_coordinates.map((coord: any) => 
          new google.maps.LatLng(coord.lat || coord[0], coord.lng || coord[1])
        );
      }

      if (paths.length < 3) return; // Need at least 3 points for a polygon

      // Determine color
      const fillColor = colorByPitch
        ? getPitchColor(facet.pitch_degrees)
        : getOrientationColor(facet.azimuth);

      // Create polygon
      const polygon = new google.maps.Polygon({
        paths: paths,
        strokeColor: selectedFacet === facet.facet_id ? '#000000' : fillColor,
        strokeOpacity: 0.8,
        strokeWeight: selectedFacet === facet.facet_id ? 3 : 1,
        fillColor: fillColor,
        fillOpacity: 0.35,
        clickable: true
      });

      polygon.setMap(googleMapInstance);

      // Add click listener
      polygon.addListener('click', () => {
        setSelectedFacet(facet.facet_id);
      });

      // Calculate center for label
      if (showLabels) {
        const bounds = new google.maps.LatLngBounds();
        paths.forEach(path => bounds.extend(path));
        const center = bounds.getCenter();

        const label = new google.maps.Marker({
          position: center,
          map: googleMapInstance,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 0
          },
          label: {
            text: `${facet.facet_id}\n${facet.area_sqft.toFixed(0)} sq ft\n${facet.pitch}`,
            color: '#000000',
            fontSize: '10px',
            fontWeight: 'bold',
            className: 'facet-label'
          }
        });

        newPolygons.push({ polygon, facet, label });
      } else {
        newPolygons.push({ polygon, facet });
      }
    });

    setFacetPolygons(newPolygons);

    // Cleanup function
    return () => {
      newPolygons.forEach(({ polygon, label }) => {
        polygon.setMap(null);
        label?.setMap(null);
      });
    };
  }, [googleMapInstance, facets, visible, showLabels, colorByPitch, selectedFacet]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const pitchGroups = facets.reduce((acc, facet) => {
      const group = facet.pitch_degrees < 18.4 ? 'low' : 
                   facet.pitch_degrees < 33.7 ? 'medium' : 'steep';
      if (!acc[group]) {
        acc[group] = { count: 0, area: 0 };
      }
      acc[group].count++;
      acc[group].area += facet.area_sqft;
      return acc;
    }, {} as Record<string, { count: number; area: number }>);

    const orientationGroups = facets.reduce((acc, facet) => {
      const cardinal = facet.orientation_cardinal;
      if (!acc[cardinal]) {
        acc[cardinal] = { count: 0, area: 0 };
      }
      acc[cardinal].count++;
      acc[cardinal].area += facet.area_sqft;
      return acc;
    }, {} as Record<string, { count: number; area: number }>);

    return { pitchGroups, orientationGroups };
  }, [facets]);

  if (!visible || facets.length === 0) {
    return null;
  }

  return (
    <Card size="sm" variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          {/* Controls */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography level="title-md" startDecorator={<Triangle size={16} />}>
              Facet Visualization
            </Typography>
            <Stack direction="row" spacing={2}>
              <FormLabel>
                <Switch
                  checked={showLabels}
                  onChange={(e) => setShowLabels(e.target.checked)}
                  size="sm"
                />
                Labels
              </FormLabel>
              <FormLabel>
                <Switch
                  checked={colorByPitch}
                  onChange={(e) => setColorByPitch(e.target.checked)}
                  size="sm"
                />
                Color by {colorByPitch ? 'Pitch' : 'Orientation'}
              </FormLabel>
            </Stack>
          </Stack>

          {/* Legend */}
          <Box>
            <Typography level="body-sm" sx={{ mb: 1 }}>
              {colorByPitch ? 'Pitch Groups' : 'Orientation'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {colorByPitch ? (
                <>
                  <Chip
                    size="sm"
                    sx={{ bgcolor: PITCH_COLORS.low, color: 'white' }}
                  >
                    Low (&lt; 4:12): {statistics.pitchGroups.low?.count || 0} facets
                  </Chip>
                  <Chip
                    size="sm"
                    sx={{ bgcolor: PITCH_COLORS.medium, color: 'black' }}
                  >
                    Medium (4-8:12): {statistics.pitchGroups.medium?.count || 0} facets
                  </Chip>
                  <Chip
                    size="sm"
                    sx={{ bgcolor: PITCH_COLORS.steep, color: 'white' }}
                  >
                    Steep (&gt; 8:12): {statistics.pitchGroups.steep?.count || 0} facets
                  </Chip>
                </>
              ) : (
                <>
                  {Object.entries(statistics.orientationGroups).map(([dir, data]) => (
                    <Chip key={dir} size="sm" variant="outlined">
                      {dir}: {data.count} facets ({data.area.toFixed(0)} sq ft)
                    </Chip>
                  ))}
                </>
              )}
            </Stack>
          </Box>

          {/* Selected Facet Info */}
          {selectedFacet && (
            <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 'sm' }}>
              <Typography level="body-sm" fontWeight="bold" sx={{ mb: 1 }}>
                Selected: Facet {selectedFacet}
              </Typography>
              {(() => {
                const facet = facets.find(f => f.facet_id === selectedFacet);
                if (!facet) return null;
                return (
                  <Stack spacing={0.5}>
                    <Typography level="body-xs">
                      Area: {facet.area_sqft.toFixed(1)} sq ft
                    </Typography>
                    <Typography level="body-xs">
                      Pitch: {facet.pitch} ({facet.pitch_degrees.toFixed(1)}°)
                    </Typography>
                    <Typography level="body-xs">
                      Orientation: {facet.orientation_cardinal} ({facet.azimuth}°)
                    </Typography>
                    <Typography level="body-xs">
                      Type: {facet.facet_type}
                    </Typography>
                  </Stack>
                );
              })()}
            </Box>
          )}

          {/* Info */}
          <Box sx={{ p: 1, bgcolor: 'neutral.50', borderRadius: 'sm' }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Info size={14} style={{ marginTop: 2 }} />
              <Typography level="body-xs">
                Click on facets to view details. Toggle between pitch and orientation coloring to analyze different aspects of the roof.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};